import { useState, useMemo } from 'react'
import { X, RefreshCw, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react'
import { renderTemplate } from './templates'
import { renderThumb, canvasToBlob, uploadThumb, thumbFilename } from './thumbRender'

// "Regenerate" for games already in the catalog: re-render the thumbnail from
// the current template + current game data, and/or re-apply the title &
// description template, and/or move the catalog event to the game's current
// start time. Pushes through the catalog's own update handler, so its live
// overlap guard and title propagation still apply.
//
// props: season, games[] (linked games to act on), templates[], thumbTemplates[],
//        ctxFor(g), sourcesFor(g), api, onDone(result), onClose
export default function RegenerateModal({ season, games, templates, thumbTemplates, ctxFor, sourcesFor, api, onDone, onClose }) {
  const defaultTpl = templates.find(t => t.is_default) || templates[0] || null
  const defaultThumb = thumbTemplates.find(t => t.is_default) || thumbTemplates[0] || null
  const [doThumb, setDoThumb] = useState(!!defaultThumb)
  const [thumbTplId, setThumbTplId] = useState(defaultThumb?.id ?? '')
  const [doText, setDoText] = useState(false)
  const [tplId, setTplId] = useState(defaultTpl?.id ?? '')
  const [doStart, setDoStart] = useState(true)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState('')
  const [result, setResult] = useState(null)
  const [err, setErr] = useState('')

  const thumbTpl = thumbTemplates.find(t => String(t.id) === String(thumbTplId)) || null
  const tpl = templates.find(t => String(t.id) === String(tplId)) || null
  const linked = useMemo(() => games.filter(g => g.tbtv_id), [games])
  const nothing = !doThumb && !doText && !doStart

  async function run() {
    if (nothing || !linked.length) return
    setBusy(true); setErr('')
    try {
      const items = []
      let i = 0
      for (const g of linked) {
        i++
        const it = { game_id: g.game_id }
        if (doThumb && thumbTpl) {
          setProgress(`Rendering thumbnail ${i} of ${linked.length}…`)
          const c = await renderThumb(thumbTpl, { ctx: ctxFor(g), sources: sourcesFor(g) })
          const blob = await canvasToBlob(c)
          const { url } = await uploadThumb(api.apiRaw, blob, thumbFilename(season, g))
          it.thumbnail_url = url
        }
        if (doText && tpl) {
          const ctx = ctxFor(g)
          it.title = renderTemplate(tpl.title_template, ctx)
          const d = renderTemplate(tpl.description_template || '', ctx)
          it.short_description = d; it.long_description = d
        }
        items.push(it)
      }
      setProgress('Updating catalog items…')
      const r = await api.regenerate(season.id, { items, update_start: doStart })
      setResult(r)
      onDone(r)
    } catch (e) {
      setErr(e.body?.error || e.message)
    } finally {
      setBusy(false); setProgress('')
    }
  }

  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="ts-modal" role="dialog" aria-modal="true" aria-labelledby="ts-regen-title">
        <div className="ts-modal-head">
          <h2 id="ts-regen-title">Regenerate {linked.length} catalog {linked.length === 1 ? 'item' : 'items'} <span className="muted">· {season.league}</span></h2>
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>

        {result ? (
          <div className="ts-sch-result">
            <p><CheckCircle size={16} className="text-success" /> Updated {result.updated.length} catalog {result.updated.length === 1 ? 'item' : 'items'}.</p>
            {result.skipped.length > 0 && <p className="muted">Skipped {result.skipped.length} ({[...new Set(result.skipped.map(s => s.reason))].join(', ')}).</p>}
            {result.errors.length > 0 && (
              <ul className="ts-sch-errors">
                {result.errors.map(e => (
                  <li key={e.game_id}><AlertCircle size={13} /> Game {e.game_id}: {e.error === 'live_schedule_overlap' ? 'live schedule overlap on the same input/channel — start time not changed' : e.error}</li>
                ))}
              </ul>
            )}
            <ul className="ts-sch-created">
              {result.updated.map(u => (
                <li key={u.tbtv_id}><a href={`/ondemand/catalog?select=${u.tbtv_id}`} target="_blank" rel="noreferrer">{u.tbtv_id} <ExternalLink size={11} /></a> <span className="muted">· {u.fields.join(', ')}</span></li>
              ))}
            </ul>
            <div className="ts-modal-actions"><span className="ts-spacer" /><button type="button" onClick={onClose}>Done</button></div>
          </div>
        ) : (
          <div className="ts-sch-body">
            <p className="muted">Re-applies current game data to the catalog items linked to the selected games. Only the parts you tick are touched; everything else on the catalog item stays as you edited it.</p>

            <label className="ts-check">
              <input type="checkbox" checked={doThumb} onChange={e => setDoThumb(e.target.checked)} disabled={!thumbTemplates.length} />
              <span>Re-render thumbnail{thumbTemplates.length ? '' : ' (no thumbnail templates yet)'}</span>
            </label>
            {doThumb && thumbTemplates.length > 1 && (
              <div className="form-group">
                <label htmlFor="ts-regen-thumbtpl">Thumbnail template</label>
                <select id="ts-regen-thumbtpl" value={thumbTplId} onChange={e => setThumbTplId(e.target.value)}>
                  {thumbTemplates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                </select>
              </div>
            )}

            <label className="ts-check">
              <input type="checkbox" checked={doText} onChange={e => setDoText(e.target.checked)} disabled={!templates.length} />
              <span>Re-apply title &amp; description template <span className="muted">(overwrites any manual edits to those fields)</span></span>
            </label>
            {doText && templates.length > 1 && (
              <div className="form-group">
                <label htmlFor="ts-regen-tpl">Template</label>
                <select id="ts-regen-tpl" value={tplId} onChange={e => setTplId(e.target.value)}>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
                </select>
              </div>
            )}

            <label className="ts-check">
              <input type="checkbox" checked={doStart} onChange={e => setDoStart(e.target.checked)} />
              <span>Move live events to the game's current start time <span className="muted">(keeps each item's duration; on-demand items unaffected)</span></span>
            </label>

            <div className="ts-sch-preview">
              {linked.map(g => (
                <div key={g.game_id} className="ts-sch-item">
                  <div className="ts-sch-item-title">{g.game_date} · {(g.game_time || '').replace(/\s*E[DS]T$/, '')} · {g.visitor} at {g.home}</div>
                  <div className="ts-sch-item-desc">{g.location} · {g.scheduled_mode === 'ondemand' ? 'On-Demand' : 'Live event'} · {g.tbtv_id}</div>
                </div>
              ))}
            </div>

            {err && <p className="ts-tpl-warn"><AlertCircle size={13} /> {err}</p>}

            <div className="ts-modal-actions">
              <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
              <span className="ts-spacer" />
              <button type="button" onClick={run} disabled={busy || nothing || !linked.length}>
                <RefreshCw size={14} className={busy ? 'spin' : ''} /> {busy ? (progress || 'Working…') : `Regenerate ${linked.length}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
